import { Controller } from "@hotwired/stimulus"

// Interactive controller shared by the Reports charts. Supports two input modes
// against the same field-binding mechanism (every "field" target whose
// data-bind-key matches a supplied map updates its textContent):
//
//   1. Tap mode (6-month trend bars): each "point" target carries a JSON map in
//      data-fields; tapping applies it and marks the bar "is-active".
//
//   2. Glide-scrubber mode (hero burndown curve): drag/tap anywhere across the
//      "plot" target — the nearest day (from the "points" value) snaps under a
//      glowing cursor with a floating tooltip, updating the header live.
//
// A "reset" target (optional) restores today's snapshot.
const GAP = 12      // px gap between tooltip card and the point
const MARGIN = 6    // px keep-clear from the chart edges
const VBW = 320     // svg viewBox width  (matches _summary_card.html.erb)
const VBH = 78      // svg viewBox height
const SAMPLES = 240 // path samples for continuous y-at-x lookup
const DRAG_SLOP = 6 // px of travel before a press counts as a drag, not a tap
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))

export default class extends Controller {
  static targets = ["point", "field", "reset", "plot", "line", "guide", "cursor", "tip", "tipCard", "bar"]
  static values = { defaultIndex: Number, points: Array }

  connect() {
    this._defaults = this.fieldTargets.map(el => ({ el, text: el.textContent }))
    this._active = false
    this._lastIdx = -1
    this._rect = null
    this._frame = null
    // Trend bars: mark today's bar active.
    if (this.hasDefaultIndexValue && this.pointTargets.length) {
      this._markActive(this.defaultIndexValue)
      // Hold the active "highlighter" shell until the entrance bar-grow ends,
      // so it reads as part of the bar instead of popping in before it.
      this._scheduleShellReveal(this.defaultIndexValue)
      // Anchored-tooltip variant (projection breakdown): park the tooltip on
      // the default point without the pop animation.
      const p = this.pointTargets[this.defaultIndexValue]
      if (p) {
        this._anchorTip(p, { pop: false })
        // Re-clamp after layout settles — connect-time rects can be stale on
        // cold open (fonts/width not final yet).
        requestAnimationFrame(() => this._clampTipCard(parseFloat(p.dataset.tipX)))
      }
    }
    // Hero scrubber: sample the curve once, then park the cursor on today.
    if (this.hasPlotTarget && this.pointsValue.length) {
      this._buildSamples()
      requestAnimationFrame(() => this._showPoint(this._defaultIdx(), { haptic: false }))
    }
  }

  disconnect() {
    if (this._frame) cancelAnimationFrame(this._frame)
    if (this._barFrame) cancelAnimationFrame(this._barFrame)
    if (this._shellFallback) clearTimeout(this._shellFallback)
    if (this._swallowTimer) clearTimeout(this._swallowTimer)
  }

  // Reveal the active-candle highlighter shell only once the entrance grow
  // animation has finished, so it lands together with the bar. On settled
  // renders (intro already played → .reveal-static, or reduced motion) there
  // is no grow, so the shell is allowed immediately. Once revealed, later
  // taps light their shell instantly (the root flag stays set).
  _scheduleShellReveal(idx) {
    const reveal = () => this.element.classList.add("reports-trend-shells-ready")

    const animating = !this.element.closest(".reveal-static") &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (!animating) { reveal(); return }

    const bar = this.pointTargets[idx]?.querySelector(".trend-grow")
    if (!bar) { reveal(); return }

    bar.addEventListener("animationend", reveal, { once: true })
    // Safety net anchored to the animation's real start (which waits for the
    // scroll-reveal): if animationend is ever missed, reveal shortly after the
    // grow's expected duration. Anchoring to start avoids firing early while
    // the card is still below the fold.
    bar.addEventListener("animationstart", () => {
      if (this._shellFallback) clearTimeout(this._shellFallback)
      this._shellFallback = setTimeout(reveal, 1100)
    }, { once: true })
  }

  // ---- Tap mode (trend bars) ------------------------------------------------

  select(event) {
    // A drag that ends over a segment still fires this segment's click. The
    // drag already chose an index; letting the click through would override it
    // with whatever happened to be under the finger at release.
    if (this._swallowClick) { this._swallowClick = false; return }
    const idx = this.pointTargets.indexOf(event.currentTarget)
    if (idx < 0) return
    this._selectIndex(idx)
  }

  _selectIndex(idx, { pop = true } = {}) {
    const point = this.pointTargets[idx]
    if (!point) return
    let map
    try { map = JSON.parse(point.dataset.fields || "{}") } catch (_) { return }
    const wasActive = point.classList.contains("is-active")
    this._applyFields(map)
    this._markActive(idx)
    this._openFor(point)
    if (!wasActive) this._replayGrow(point)
    this._anchorTip(point, { pop })
    if (this.hasResetTarget) this.resetTarget.classList.toggle("hidden", idx === this.defaultIndexValue)
    this._haptic()
  }

  // ---- Bar-scrub mode (projection breakdown) --------------------------------
  //
  // Segments are sized in proportion to their amount, so a small budget can
  // render only a few pixels wide and is effectively impossible to tap.
  //
  // A TAP stays positional: touch the big block, get that budget, exactly as
  // before. A DRAG switches to equal slices — while the finger is down every
  // segment owns the same share of the bar's width, so a 1% budget takes as
  // much travel to reach as a 60% one. The tooltip carries the same actions,
  // so it doubles as the drag handle.

  // A card may hold several bars (net worth: what you have over what you
  // owe). The scrub follows the bar the finger went down on and walks only the
  // points inside it, so each bar keeps its own equal slices.
  barStart(event) {
    if (!this.hasBarTarget) return
    this._bar = this.barTargets.find(bar => bar.contains(event.currentTarget)) || this.barTarget
    // A fresh press means the previous gesture's click can no longer arrive, so
    // any armed swallow is stale. Without this, a drag that ends without a
    // click — released on the tooltip, in a segment gap, or cancelled when the
    // browser claims the vertical axis, and on iOS every drag past the slop,
    // where WebKit suppresses the synthetic click — leaves the flag armed and
    // eats the next real tap.
    this._swallowClick = false
    if (this._swallowTimer) clearTimeout(this._swallowTimer)
    this._barRect = this._bar.getBoundingClientRect()
    this._barStartX = event.clientX
    this._barDragging = false
  }

  barMove(event) {
    if (!this._barRect) return
    if (!this._barDragging) {
      // Below the slop threshold this is still a tap in progress: leave the
      // click path alone so segment taps keep working.
      if (Math.abs(event.clientX - this._barStartX) < DRAG_SLOP) return
      this._barDragging = true
      try { event.currentTarget.setPointerCapture(event.pointerId) } catch (_) { /* noop */ }
      this._bar.classList.add("pb-scrubbing")
    }
    this._queueBarStep(event.clientX)
    event.preventDefault()
  }

  barEnd() {
    if (!this._barRect) return
    if (this._barDragging) {
      this._swallowClick = true
      // If the release lands outside any segment no click follows, so the flag
      // would sit armed and eat the next real tap.
      if (this._swallowTimer) clearTimeout(this._swallowTimer)
      this._swallowTimer = setTimeout(() => { this._swallowClick = false }, 350)
    }
    this._barRect = null
    this._barDragging = false
    this._bar.classList.remove("pb-scrubbing")
  }

  // Coalesce rapid pointermove events into one paint per frame.
  _queueBarStep(clientX) {
    this._barQueueX = clientX
    if (this._barFrame) return
    this._barFrame = requestAnimationFrame(() => {
      this._barFrame = null
      this._barStep(this._barQueueX)
    })
  }

  _barStep(clientX) {
    const bar = this._bar || this.barTarget
    const inBar = this.pointTargets.filter(point => bar.contains(point))
    const points = inBar.length ? inBar : this.pointTargets
    const n = points.length
    if (!n) return
    const r = this._barRect || bar.getBoundingClientRect()
    let rel = clamp((clientX - r.left) / r.width, 0, 1)
    // The bar renders dir="rtl" in Arabic, so the first segment sits on the
    // right and the mapping has to run the other way.
    if (getComputedStyle(bar).direction === "rtl") rel = 1 - rel

    const point = points[clamp(Math.floor(rel * n), 0, n - 1)]
    if (point.classList.contains("is-active")) return
    this._selectIndex(this.pointTargets.indexOf(point))
  }

  // Replay the trend-grow rise on the newly selected candle (trend bars).
  // Zero the inline entrance stagger so the replay starts instantly, then
  // restart the keyframe via class re-add + forced reflow.
  _replayGrow(point) {
    const bar = point.querySelector(".reports-trend-bar")
    if (!bar) return
    bar.style.animationDelay = "0ms"
    bar.classList.remove("trend-grow")
    void bar.offsetWidth // restart the keyframe animation
    bar.classList.add("trend-grow")
  }

  // Tap mode with an anchored tooltip: glide the tooltip to the tapped point
  // (data-tip-x = center %), recolor it, and replay the pop animation.
  // No-op for cards without a tip target or untagged points (trend bars).
  _anchorTip(point, { pop = true } = {}) {
    if (!this.hasTipTarget || !point.dataset.tipX) return
    this.tipTarget.style.left = point.dataset.tipX + "%"
    if (point.dataset.tipColor) this.tipTarget.style.setProperty("--tip-color", point.dataset.tipColor)
    this._clampTipCard(parseFloat(point.dataset.tipX))
    if (pop && this.hasTipCardTarget) {
      this.tipCardTarget.classList.remove("pb-pop")
      void this.tipCardTarget.offsetWidth // restart the keyframe animation
      this.tipCardTarget.classList.add("pb-pop")
    }
  }

  // Keep the anchored tooltip card inside its container. The .pb-tip anchor
  // stays glued to the segment center (so the glide transition is untouched);
  // the card slides relative to the anchor when it would clip an edge, and
  // the arrow counter-slides so it keeps pointing at the segment. Computed
  // from the anchor's FINAL position (the % we just set), never the
  // mid-transition position — so the clamp always lands where the glide ends.
  _clampTipCard(xPct) {
    if (!this.hasTipCardTarget || Number.isNaN(xPct)) return
    const wrap = this.tipTarget.offsetParent
    if (!wrap) return
    const ww = wrap.clientWidth
    if (!ww) return
    const anchor = (xPct / 100) * ww
    const cw = this.tipCardTarget.offsetWidth
    const half = cw / 2
    let shift = 0
    if (anchor - half < MARGIN) {
      shift = MARGIN - (anchor - half)
    } else if (anchor + half > ww - MARGIN) {
      shift = (ww - MARGIN) - (anchor + half)
    }
    this.tipCardTarget.style.left = `${Math.round(shift)}px`
    const arrow = this.tipCardTarget.querySelector(".pb-tip-arrow")
    if (arrow) {
      // Arrow center within the card: card center minus the card's shift,
      // clamped clear of the rounded corners.
      const arrowX = clamp(half - shift, 10, cw - 10)
      arrow.style.left = `${Math.round(arrowX)}px`
    }
  }

  // ---- Glide-scrubber mode (hero burndown curve) ----------------------------

  start(event) {
    if (!this.hasPlotTarget) return
    this._active = true
    // Cache geometry for the whole drag — no per-move layout reads (key to a
    // smooth 60fps glide). Capture on whichever handle was grabbed (plot,
    // cursor dot, or tooltip) so the drag keeps tracking the finger.
    this._rect = this.plotTarget.getBoundingClientRect()
    if (!this._samples) this._buildSamples()
    this.plotTarget.classList.add("rc-dragging")
    // Mascot swap: peek-magnifier appears instantly while the finger is down.
    this.element.classList.add("rc-holding")
    try { event.currentTarget.setPointerCapture(event.pointerId) } catch (_) { /* noop */ }
    this._queueGlide(event.clientX)
    event.preventDefault()
  }

  move(event) { if (this._active) this._queueGlide(event.clientX) }

  end() {
    this._active = false
    this._rect = null
    // Back to rest: header keeps the last day, hint pill reappears on the cursor.
    this.plotTarget.classList.remove("rc-dragging")
    // Mascot swap back: side-winking returns through the CSS blur cross-fade.
    this.element.classList.remove("rc-holding")
  }

  _defaultIdx() {
    return this.hasDefaultIndexValue ? this.defaultIndexValue : this.pointsValue.length - 1
  }

  // Coalesce rapid pointermove events into one paint per frame.
  _queueGlide(clientX) {
    this._queueX = clientX
    if (this._frame) return
    this._frame = requestAnimationFrame(() => {
      this._frame = null
      this._glide(this._queueX)
    })
  }

  // Continuous scrub: the cursor follows the finger's exact x and rides the
  // real curve (y interpolated from path samples); the header/tooltip value
  // snaps to the nearest day so the number stays meaningful.
  _glide(clientX) {
    const pts = this.pointsValue
    if (!pts.length) return
    const r = this._rect || this.plotTarget.getBoundingClientRect()
    const rel = clamp((clientX - r.left) / r.width, 0, 1)
    const xPct = rel * 100
    const yPct = (this._yAtX(rel * VBW) / VBH) * 100

    this.plotTarget.classList.add("rc-live")
    this.guideTarget.style.left = xPct + "%"
    this.cursorTarget.style.left = xPct + "%"
    this.cursorTarget.style.top = yPct + "%"
    this.tipTarget.style.left = xPct + "%"
    this.tipTarget.style.top = yPct + "%"

    let best = 0, bd = Infinity
    for (let i = 0; i < pts.length; i++) {
      const dd = Math.abs(pts[i].x / 100 - rel)
      if (dd < bd) { bd = dd; best = i }
    }
    const p = pts[best]
    this._placeTip(xPct, yPct, r)
    this._applyFields(p.f)
    if (this.hasResetTarget) this.resetTarget.classList.toggle("hidden", best === this._defaultIdx())
    if (best !== this._lastIdx) { this._lastIdx = best; this._haptic() }
  }

  // Sample the drawn curve into an x-sorted lookup table (viewBox coords).
  // Independent of pixel size, so it survives resizes without rebuilding.
  _buildSamples() {
    this._samples = null
    const path = this.hasLineTarget ? this.lineTarget : null
    if (!path || typeof path.getTotalLength !== "function") return
    const len = path.getTotalLength()
    if (!len) return
    const out = []
    for (let i = 0; i <= SAMPLES; i++) {
      const pt = path.getPointAtLength((len * i) / SAMPLES)
      out.push({ x: pt.x, y: pt.y })
    }
    out.sort((a, b) => a.x - b.x)
    this._samples = out
  }

  _yAtX(x) {
    const s = this._samples
    if (!s || !s.length) return VBH / 2
    if (x <= s[0].x) return s[0].y
    if (x >= s[s.length - 1].x) return s[s.length - 1].y
    let lo = 0, hi = s.length - 1
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1
      if (s[mid].x < x) lo = mid; else hi = mid
    }
    const a = s[lo], b = s[hi]
    const t = (x - a.x) / ((b.x - a.x) || 1)
    return a.y + (b.y - a.y) * t
  }

  // Park the cursor + rest hint on an exact data point (default load / reset).
  // haptic=false suppresses the buzz for these programmatic shows.
  _showPoint(i, { haptic = true } = {}) {
    const p = this.pointsValue[i]
    this.plotTarget.classList.add("rc-live")

    this.guideTarget.style.left = p.x + "%"
    this.cursorTarget.style.left = p.x + "%"
    this.cursorTarget.style.top = p.y + "%"

    this.tipTarget.style.left = p.x + "%"
    this.tipTarget.style.top = p.y + "%"
    this._placeTip(p.x, p.y)

    this._applyFields(p.f)
    if (this.hasResetTarget) this.resetTarget.classList.toggle("hidden", i === this._defaultIdx())
    if (haptic && i !== this._lastIdx) { this._lastIdx = i; this._haptic() }
  }

  // Keep the tooltip inside the chart: clamp horizontally so it never clips an
  // edge, and flip below the point when there is not enough room above it.
  _placeTip(xPct, yPct, rect = null) {
    const r = rect || this.plotTarget.getBoundingClientRect()
    const cardR = this.element.getBoundingClientRect()
    const px = (xPct / 100) * r.width
    const py = (yPct / 100) * r.height
    const cw = this.tipCardTarget.offsetWidth
    const ch = this.tipCardTarget.offsetHeight

    const minLeft = MARGIN - px
    const maxLeft = r.width - MARGIN - cw - px
    const left = maxLeft < minLeft ? minLeft : clamp(-cw / 2, minLeft, maxLeft)
    this.tipCardTarget.style.left = left + "px"

    const pointInCardY = (r.top - cardR.top) + py
    const above = (pointInCardY - GAP - ch) >= MARGIN
    this.tipCardTarget.style.top = (above ? -(ch + GAP) : GAP) + "px"
  }

  // ---- Shared ---------------------------------------------------------------

  reset() {
    this._defaults.forEach(({ el, text }) => { el.textContent = text })
    if (this.hasDefaultIndexValue && this.pointTargets.length) this._markActive(this.defaultIndexValue)
    if (this.hasResetTarget) this.resetTarget.classList.add("hidden")
    this._lastIdx = -1
    // Hero scrubber: return to today's point (tooltip shown, no extra buzz).
    if (this.hasPlotTarget && this.pointsValue.length) this._showPoint(this._defaultIdx(), { haptic: false })
    this._haptic()
  }

  _applyFields(map) {
    this.fieldTargets.forEach(el => {
      const key = el.dataset.bindKey
      if (key && Object.prototype.hasOwnProperty.call(map, key)) el.textContent = map[key]
    })
  }

  // Marks the selected point. When points carry `data-pair-key`, every point
  // sharing that key lights up together — this is what lets one control be
  // rendered twice (a ring segment and its list row) and stay in lockstep.
  // Without the attribute the behaviour is unchanged: one index, one active.
  // `data-accent` publishes the selection's colour as `--accent` on the root
  // so dependent styling (centre amount, row rail) follows without extra JS.
  _markActive(idx) {
    const selected = this.pointTargets[idx]
    const pairKey = selected?.dataset.pairKey

    this.pointTargets.forEach((p, i) => {
      const active = pairKey ? p.dataset.pairKey === pairKey : i === idx
      p.classList.toggle("is-active", active)
    })

    if (selected?.dataset.accent) {
      this.element.style.setProperty("--accent", selected.dataset.accent)
    }
  }

  // A point may name a <details> to open when it is picked (data-opens="id"):
  // on net worth, a bar segment opens the band that lists it. Never closes.
  _openFor(point) {
    const id = point.dataset.opens
    if (!id) return
    const details = document.getElementById(id)
    if (details && !details.open) details.open = true
  }

  _haptic() {
    window.dispatchEvent(new CustomEvent("bridge:haptic", { detail: { style: "light" } }))
  }
}

